import React, { ReactNode } from 'react';
import { Stack } from '@moda/om';
import { ApolloError } from '@apollo/client';
import { noop } from '@moda/portal-stanchions';
import { SortOption } from '../../constants';
import { SidebarLayout, Sidebar, Content } from '../SidebarLayout';
import { PageFilter, PageFilterClear } from '../PageFilter';
import { GridItemCount } from '../GridPeripherals/GridItemCount';
import { GridSortByDropdown } from '../GridPeripherals/GridSortByDropdown';
import { SalePercentageTags } from '../SalePercentageTags/SalePercentageTags';
import { PLPBreadcrumbs } from '../PLPBreadcrumbs';
import { GuidePageQuery, ProductListPageResultFragment } from '../../generated/types';

import './ProductListingDesktop.scss';

type DisplayPage = NonNullable<GuidePageQuery['displayPage']>;

type Props = {
  currentSort: SortOption;
  onSortChanged: (sort: string) => void;
  availableSorts: SortOption[];
  headerText?: string;
  error?: ApolloError;
  description?: string;
  showPLPBreadcrumbs?: boolean;
  preserveBreadcrumbSpace?: boolean;
  hideControls?: boolean;
  displayPage?: DisplayPage | null;
  queryResult?: ProductListPageResultFragment | null;
  belowHeader?: ReactNode;
  belowLayout?: ReactNode;
  children?: React.ReactNode;
};

export const ProductListingDesktop: React.FC<Props> = ({
  children,
  headerText,
  currentSort,
  queryResult,
  displayPage,
  onSortChanged,
  availableSorts,
  showPLPBreadcrumbs,
  preserveBreadcrumbSpace,
  hideControls,
  belowHeader,
  belowLayout
}) => {
  const totalItems = queryResult?.totalItems || 0;

  return (
    <>
      {showPLPBreadcrumbs ? (
        <PLPBreadcrumbs />
      ) : preserveBreadcrumbSpace ? (
        <div className="ProductListingDesktop__breadcrumbs-placeholder" aria-hidden>
          <PLPBreadcrumbs />
        </div>
      ) : null}

      <h1 className="ProductListingDesktop__header" data-testid="ProductListingDesktop__header">
        {headerText}
      </h1>

      {belowHeader}

      <SalePercentageTags saleTags={queryResult?.saleTags || []} />

      {!hideControls && (
        <Stack
          space={4}
          direction="horizontal"
          justifyContent="flex-end"
          alignItems="center"
          className="ProductListingDesktop__grid-container"
        >
          <GridItemCount count={totalItems} />

          <GridSortByDropdown
            currentSort={currentSort}
            availableSorts={availableSorts}
            onSortByChange={option => onSortChanged(option)}
          />
        </Stack>
      )}

      <SidebarLayout>
        {!hideControls && (
          <Sidebar stickAt={150}>
            <PageFilter
              attributes={queryResult?.attributes || []}
              availability={queryResult?.availability || null}
              categoryTree={queryResult?.categoryTree || null}
              colors={queryResult?.filterColors || []}
              designers={queryResult?.designers || []}
              saleTags={queryResult?.saleTags || []}
              sizes={queryResult?.sizes || []}
              guideSubPages={displayPage?.guideSubPages || null}
              onClose={noop}
            />
          </Sidebar>
        )}

        <Content>
          <Stack space={3}>
            <PageFilterClear
              attributes={queryResult?.attributes || []}
              designers={queryResult?.designers || []}
              sizes={queryResult?.sizes || []}
              categoryTree={queryResult?.categoryTree || null}
            />
            {children}
          </Stack>
        </Content>
      </SidebarLayout>

      {belowLayout}
    </>
  );
};
